import { calculateComponentArea } from 'features/fde/areaCalculations'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

function areaSqMeters(num: number): GeckoMeasurement | undefined {
  if (!Number.isFinite(num) || num < 0) return undefined
  return GeckoMeasurement.CreateFromValue(
    num,
    GeckoMeasurementUnit.SquareMeters
  )
}

function meters(m: GeckoMeasurement): number {
  return m.convertTo(GeckoMeasurementUnit.Meters).num
}

type AreaSqMetersByType = {
  [K in GeckoComponentModelType]: (
    model: Extract<GeckoComponentModel, { type: K }>
  ) => number | undefined
}

function computeAreaSqMetersFromFde(
  component: GeckoComponentModel
): number | undefined {
  const m = calculateComponentArea(component).convertTo(
    GeckoMeasurementUnit.SquareMeters
  )
  return Number.isFinite(m.num) && m.num >= 0 ? m.num : undefined
}

const areaSqMetersByType = {
  [GeckoComponentModelType.HorizontalTankShell]: (model) => {
    const r = meters(model.radius)
    const L = meters(model.length)
    return 2 * Math.PI * r * L
  },

  [GeckoComponentModelType.VerticalTankShell]: (model) => {
    const r = meters(model.radius)
    const h = meters(model.height)
    return 2 * Math.PI * r * h
  },

  [GeckoComponentModelType.TankFlatRoof]: (model) => {
    const r = meters(model.radius)
    return Math.PI * r * r
  },

  [GeckoComponentModelType.TankFloor]: (model) => {
    const r = meters(model.radius)
    return Math.PI * r * r
  },

  [GeckoComponentModelType.PipeSection]: (model) => {
    const r = meters(model.radius)
    const L = meters(model.length)
    return 2 * Math.PI * r * L
  },

  [GeckoComponentModelType.PipeElbow]: (model) => {
    // Torus surface area scaled by angular sweep:
    // full torus: (2πR)(2πr) = 4π² R r
    const r = meters(model.radius)
    const R = meters(model.majorRadius)
    const frac = model.angle / 360
    return 4 * Math.PI * Math.PI * R * r * frac
  },

  [GeckoComponentModelType.PipeTJoint]: (model) => {
    // Best-effort approximation: sum of lateral areas of the chord and brace,
    // ignoring the overlap at the intersection.
    const brace =
      2 * Math.PI * meters(model.brace_radius) * meters(model.brace_length)
    const chord =
      2 * Math.PI * meters(model.chord_radius) * meters(model.chord_length)
    return brace + chord
  },

  [GeckoComponentModelType.PipeEccentricReducer]: (model) => {
    // Approximate as a conical frustum using axial length.
    const L = meters(model.length)
    const r1 = meters(model.inlet_radius)
    const r2 = meters(model.outlet_radius)
    const dr = r1 - r2
    const s = Math.sqrt(dr * dr + L * L) // slant height
    return Math.PI * (r1 + r2) * s
  },

  [GeckoComponentModelType.LegacyBoilerWalls]: (model) => {
    // Approximate as a rectangle: height * (tubeCount * tubeSpacing).
    // If tube spacing isn't defined, fall back to the default used elsewhere.
    const tubeSpacingM =
      model.tubeSpacing?.convertTo(GeckoMeasurementUnit.Meters).num ?? 0.0508
    const widthM = model.tubeCount.num * tubeSpacingM
    const heightM = meters(model.height)
    return widthM * heightM
  },

  [GeckoComponentModelType.ShipDeck]: (model) => {
    // Trapezoid area with varying width across the length.
    const L = meters(model.length)
    const w0 = meters(model.startWidth)
    const w1 = meters(model.endWidth)
    return L * (w0 + w1) * 0.5
  },

  [GeckoComponentModelType.BasicShipHull]: () => {
    // Model-ID-only; no reliable geometric parameters.
    return undefined
  },

  [GeckoComponentModelType.LegacyCone]: (model) => {
    // Cones are plotted in a radial plane (x=degrees, y=radius). To match coverage
    // calculations, use the annulus area: π(R² - r²).
    const innerR = meters(model.componentBounds.start.y)
    const outerR = meters(model.componentBounds.end.y)
    if (outerR < innerR) return undefined
    return Math.PI * (outerR * outerR - innerR * innerR)
  },

  [GeckoComponentModelType.Cone]: (model) => {
    // Cones are plotted in a radial plane (x=degrees, y=radius). To match coverage
    // calculations, use the annulus area: π(R² - r²).
    const innerR = meters(model.componentBounds.start.y)
    const outerR = meters(model.componentBounds.end.y)
    if (outerR < innerR) return undefined
    return Math.PI * (outerR * outerR - innerR * innerR)
  },

  [GeckoComponentModelType.LaunchTube]: (model) => {
    const r = meters(model.radius)
    const h = meters(model.height)
    return 2 * Math.PI * r * h
  },

  [GeckoComponentModelType.Dome]: (model) => {
    // Hemispherical dome
    const r = meters(model.radius)
    return 2 * Math.PI * r * r
  },

  [GeckoComponentModelType.DomeWall]: (model) => {
    const r = meters(model.radius)
    const h = meters(model.height)
    return 2 * Math.PI * r * h
  },

  [GeckoComponentModelType.FlatCircularFloor]: (model) => {
    const r = meters(model.radius)
    return Math.PI * r * r
  },

  [GeckoComponentModelType.VerticalTankFoundation]: (model) => {
    // Best-effort: treat as cylindrical wall.
    const r = meters(model.radius)
    const h = meters(model.height)
    return 2 * Math.PI * r * h
  },

  [GeckoComponentModelType.MachinedForgingSimple]: () => {
    // Model-only; no reliable geometric parameters.
    return undefined
  },

  [GeckoComponentModelType.PressureVesselShell]: (model) => {
    const r = meters(model.radius)
    const L = meters(model.length)
    return 2 * Math.PI * r * L
  },

  [GeckoComponentModelType.HemisphericalHead]: (model) => {
    const r = meters(model.radius)
    return 2 * Math.PI * r * r
  },

  [GeckoComponentModelType.TorisphericalHead]: (model) => {
    // Best-effort approximation: treat as hemispherical using crownRadius.
    const r = meters(model.crownRadius)
    return 2 * Math.PI * r * r
  },

  [GeckoComponentModelType.EllipsoidalHead]: (model) => {
    // Half of a spheroid (surface of revolution). Uses exact formula for oblate/prolate spheroids.
    const a = meters(model.radius) // equatorial radius
    const c = meters(model.minorRadius) // polar radius
    if (a <= 0 || c <= 0) return undefined

    if (Math.abs(a - c) < 1e-12) {
      // Sphere
      return 2 * Math.PI * a * a
    }

    let full: number
    if (c < a) {
      // Oblate spheroid
      const e = Math.sqrt(1 - (c * c) / (a * a))
      const atanhE = 0.5 * Math.log((1 + e) / (1 - e))
      full = 2 * Math.PI * a * a * (1 + ((1 - e * e) / e) * atanhE)
    } else {
      // Prolate spheroid
      const e = Math.sqrt(1 - (a * a) / (c * c))
      full = 2 * Math.PI * a * a * (1 + (c / (a * e)) * Math.asin(e))
    }
    return full * 0.5
  },

  [GeckoComponentModelType.SphereFlatCap]: (model) => {
    // Best-effort: hemispherical shell + flat circular cap.
    const r = meters(model.radius)
    return 3 * Math.PI * r * r
  },

  [GeckoComponentModelType.HoodStraight]: (model) => {
    const r = meters(model.radius)
    const L = meters(model.length)
    return 2 * Math.PI * r * L
  },

  [GeckoComponentModelType.HoodBend]: (model) => {
    const R = meters(model.majorRadius)
    const r = meters(model.minorRadius)
    const frac = model.torusAngle / 360
    return 4 * Math.PI * Math.PI * R * r * frac
  },
} satisfies AreaSqMetersByType

function computeAreaSqMeters<M extends GeckoComponentModel>(
  model: M
): number | undefined {
  const fde = computeAreaSqMetersFromFde(model)
  if (fde !== undefined) return fde

  // TS can't infer that the indexed function accepts `M`, so we connect them here.
  const fn = areaSqMetersByType[model.type] as (m: M) => number | undefined
  return fn(model)
}

/**
 * Attempts to compute the total "model area" for a component.
 *
 * Notes:
 * - Where possible, we delegate to `features/fde/areaCalculations` to stay
 *   consistent with how inspection coverage is computed.
 * - Many components here represent *shell* surfaces (e.g. cylinders) so end-caps
 *   are typically not included unless explicitly part of the component type.
 * - Some component types do not contain enough geometric info (e.g. model-ID-only
 *   ship hulls), and will return undefined.
 */
export function computeArea(
  component: GeckoComponentModel
): GeckoMeasurement | undefined {
  const num = computeAreaSqMeters(component)
  return num === undefined ? undefined : areaSqMeters(num)
}
